import { Lesson } from '../../api/types/lesson';
import { ContentGroup } from '../../api/types/contentGroup';
import React, { useContext, useEffect, useState } from 'react';
import { AppSettingsContext } from '../../providers/AppSettingsProvider/context';
import { CustomContentInsight } from './types';
import { Components } from './styled';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { getInsights } from './utils';
import { ReactComponent as InsightsIcon } from '../../assets/icons/icon-exclamation-circle-blue.svg';
import { Tooltip } from '@mui/material';

const RenderTooltip = (_contentInsights: CustomContentInsight[]) => {
  return (
    <Components.Container fullWidth fullHeight>
      <Components.InsightsHeader>
        <Components.InsightsTitle>Content Management Insights</Components.InsightsTitle>
        {_contentInsights.map((insight, index) => (
          <Components.InsightsItemWrapper key={`insight-wrapper-${index}`}>
            <Components.IconWrapper>
              <InsightsIcon />
            </Components.IconWrapper>
            <Components.InsightsItem key={`insight-${index}`}>{insight.insightText}</Components.InsightsItem>
          </Components.InsightsItemWrapper>
        ))}
      </Components.InsightsHeader>
    </Components.Container>
  );
};

const InsightsCardSmall = ({ contentInsights }: { contentInsights: CustomContentInsight[] }) => {
  return (
    <Tooltip title={RenderTooltip(contentInsights)} placement={'top'} arrow>
      <div>
        <Components.InsightsCard>
          <Components.InsightsItem>
            {`${contentInsights.length} ` + (contentInsights.length === 1 ? 'insight' : 'insights') + ' to review'}
          </Components.InsightsItem>
        </Components.InsightsCard>
      </div>
    </Tooltip>
  );
};

const InsightsCardDetailed = ({ contentInsights }: { contentInsights: CustomContentInsight[] }) => {
  return (
    <Components.InsightsHeader>
      <Components.InsightsTitle>Content Management Insights</Components.InsightsTitle>
      {contentInsights.map((insight, index) => (
        <Components.InsightsItemWrapper key={`insight-wrapper-${index}`}>
          <Components.IconWrapper>
            <InsightsIcon />
          </Components.IconWrapper>
          <Components.InsightsItem key={`insight-${index}`}>{insight.insightText}</Components.InsightsItem>
        </Components.InsightsItemWrapper>
      ))}
    </Components.InsightsHeader>
  );
};

const InsightsCardCell = ({ contentInsights }: { contentInsights: CustomContentInsight[] }) => {
  return (
    <Tooltip title={RenderTooltip(contentInsights)} placement={'top'} arrow>
      <div>
        <Components.Container fullWidth fullHeight displayFlex alignItems={'center'} justifyContent={'flex-start'}>
          <Components.InsightsCard>
            <Components.InsightsItem>{contentInsights.length}</Components.InsightsItem>
            <Components.IconWrapper>
              <InsightsIcon />
            </Components.IconWrapper>
          </Components.InsightsCard>
        </Components.Container>
      </div>
    </Tooltip>
  );
};

const InsightsCardFieldAdornment = ({
  fieldName,
  contentInsights,
}: {
  fieldName?: string;
  contentInsights: CustomContentInsight[];
}) => {
  if (!fieldName) {
    console.log(
      `[INSIGHTS] Warning: fieldName is required for cardStyle "fieldAdornment" - component did not render correctly.`,
    );
    return <></>;
  }
  const fieldInsight = contentInsights.filter((item) => item.fieldKey === fieldName)?.[0];
  if (!fieldInsight?.insightText) {
    return <></>;
  }

  return (
    <Components.Container fullWidth fullHeight displayFlex alignItems={'flex-start'} justifyContent={'flex-start'}>
      <Components.InsightsItemWrapper>
        <Components.IconWrapper>
          <InsightsIcon />
        </Components.IconWrapper>
        <Components.InsightsItem>{fieldInsight.insightText}</Components.InsightsItem>
      </Components.InsightsItemWrapper>
    </Components.Container>
  );
};

const InsightsCardDailyAluxAdornment = ({ contentInsights }: { contentInsights: CustomContentInsight[] }) => {
  return (
    <Tooltip title={RenderTooltip(contentInsights)} placement={'top'} arrow>
      <div>
        <Components.IconWrapper>
          <InsightsIcon />
        </Components.IconWrapper>
      </div>
    </Tooltip>
  );
};

const ContentInsightsCard = ({
  content,
  contentType,
  params,
  cardStyle,
  fieldName,
}: {
  content?: Lesson | ContentGroup;
  contentType: 'lesson' | 'contentGroup';
  params?: GridRenderCellParams;
  cardStyle: 'small' | 'detailed' | 'cell' | 'fieldAdornment' | 'dailyAluxAdornment';
  fieldName?: string;
}) => {
  const { categories, goals } = useContext(AppSettingsContext);

  const [contentInsights, setContentInsights] = useState<CustomContentInsight[]>([]);

  useEffect(() => {
    setContentInsights(getInsights(content || params?.row?.fullContentData, contentType, categories, goals));
  }, [categories, goals, content, params]);

  return (
    <>
      {contentInsights.length ? (
        cardStyle === 'small' ? (
          <InsightsCardSmall contentInsights={contentInsights} />
        ) : cardStyle === 'detailed' ? (
          <InsightsCardDetailed contentInsights={contentInsights} />
        ) : cardStyle === 'cell' ? (
          <InsightsCardCell contentInsights={contentInsights} />
        ) : cardStyle === 'fieldAdornment' ? (
          <InsightsCardFieldAdornment fieldName={fieldName} contentInsights={contentInsights} />
        ) : cardStyle === 'dailyAluxAdornment' ? (
          <InsightsCardDailyAluxAdornment contentInsights={contentInsights} />
        ) : (
          <></>
        )
      ) : (
        <></>
      )}
    </>
  );
};

export default ContentInsightsCard;
